import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, TemplateRef, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ComplianceResult, ComplianceResultLabelKeys, ComplianceResultLabels } from '../../enums/compliance-result.enum';
import {
  ChecklistTableActionEvent,
  ChecklistTableCheckboxEvent,
  ChecklistTableColumn,
  ChecklistTableGroup,
  ChecklistTableSectionConfig,
  ChecklistTableStatus,
  ChecklistTableToggleEvent,
  ChecklistTableType
} from './checklist-table.model';

@Component({
  selector: 'app-checklist-table',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  templateUrl: './checklist-table.component.html',
  styleUrl: './checklist-table.component.scss'
})
export class ChecklistTableComponent {
  private readonly transloco = inject(TranslocoService);

  @Input() sections: ChecklistTableSectionConfig[] = [];
  @Input() groups: ChecklistTableGroup[] = [];
  @Input() rowTemplate?: TemplateRef<{ $implicit: unknown; group: ChecklistTableGroup }>;
  @Input() groupFooterTemplate?: TemplateRef<{ $implicit: ChecklistTableGroup }>;

  @Output() checkboxChanged = new EventEmitter<ChecklistTableCheckboxEvent>();
  @Output() actionClicked = new EventEmitter<ChecklistTableActionEvent>();
  @Output() toggleChanged = new EventEmitter<ChecklistTableToggleEvent>();

  private readonly expandedGroupIds = new Set<string | number>();

  getSectionGroups(section: ChecklistTableSectionConfig): ChecklistTableGroup[] {
    return this.groups.filter(group => this.matchesSectionType(group.type, section) && group.rows.length > 0);
  }

  toggleGroup(group: ChecklistTableGroup): void {
    if (this.expandedGroupIds.has(group.id)) {
      this.expandedGroupIds.delete(group.id);
    } else {
      this.expandedGroupIds.add(group.id);
    }
  }

  isGroupExpanded(group: ChecklistTableGroup): boolean {
    return this.expandedGroupIds.has(group.id);
  }

  showColumnHeaders(section: ChecklistTableSectionConfig): boolean {
    return section.showColumnHeaders !== false;
  }

  getGroupStatus(group: ChecklistTableGroup, section: ChecklistTableSectionConfig): { label: string; class: string } | null {
    const statusColumn = section.columns.find(column => column.type === 'status');
    if (!statusColumn || group.rows.length === 0) {
      return null;
    }

    const rank: Record<string, number> = { fail: 3, pending: 2, pass: 1 };
    const worstRow = group.rows.reduce((worst, row) =>
      (rank[this.getStatusClass(row, statusColumn)] ?? 0) > (rank[this.getStatusClass(worst, statusColumn)] ?? 0) ? row : worst);

    return {
      label: this.getStatusLabel(worstRow, statusColumn),
      class: this.getStatusClass(worstRow, statusColumn)
    };
  }

  getColumnHeader(column: ChecklistTableColumn): string {
    return column.header ?? '';
  }

  getGridTemplate(columns: ChecklistTableColumn[]): string {
    return columns.map((column, index) => column.width ?? (index === 0 ? '2fr' : '1fr')).join(' ');
  }

  getCellValue(row: any, column: ChecklistTableColumn): any {
    return row[column.value];
  }

  hasLinkUrl(row: any, column: ChecklistTableColumn): boolean {
    return !!column.linkUrlValue && !!row[column.linkUrlValue];
  }

  isMutedValue(row: any, column: ChecklistTableColumn): boolean {
    return !!column.mutedValue && this.getCellValue(row, column) === column.mutedValue;
  }

  getSubLabel(row: any, column: ChecklistTableColumn): string | null {
    return column.subLabelValue ? (row[column.subLabelValue] ?? null) : null;
  }

  isAttachUnavailable(row: any, column: ChecklistTableColumn): boolean {
    return !!column.attachUnavailableValue && !!row[column.attachUnavailableValue];
  }

  getStatusLabel(row: any, column: ChecklistTableColumn): string {
    const labelValue = column.labelValue ? row[column.labelValue] : null;
    if (labelValue) {
      return labelValue;
    }

    const value = this.getCellValue(row, column) as ComplianceResult;
    const labelKey = ComplianceResultLabelKeys[value];
    return labelKey ? this.transloco.translate(labelKey) : `${value ?? ''}`;
  }

  getStatusClass(row: any, column: ChecklistTableColumn): string {
    const value = this.getCellValue(row, column) as ChecklistTableStatus;
    const label = typeof value === 'number'
      ? ComplianceResultLabels[value as ComplianceResult]
      : `${value ?? ''}`;

    return label.toLowerCase();
  }

  onCheckboxChange(event: Event, row: any, group: ChecklistTableGroup, column: ChecklistTableColumn): void {
    const checkbox = event.target as HTMLInputElement;
    row[column.value] = checkbox.checked;
    this.checkboxChanged.emit({ row, group, column, checked: checkbox.checked });
  }

  onToggleChange(event: Event, row: any, group: ChecklistTableGroup, column: ChecklistTableColumn): void {
    const checkbox = event.target as HTMLInputElement;
    row[column.value] = checkbox.checked;
    this.toggleChanged.emit({ row, group, column, checked: checkbox.checked });
  }

  onActionClick(event: Event, row: any, group: ChecklistTableGroup, column: ChecklistTableColumn): void {
    event.stopPropagation();
    this.actionClicked.emit({
      row,
      group,
      column,
      actionName: column.actionName ?? column.value
    });
  }

  onAttachClick(event: Event, row: any, group: ChecklistTableGroup, column: ChecklistTableColumn): void {
    event.stopPropagation();
    this.actionClicked.emit({
      row,
      group,
      column,
      actionName: column.attachActionName ?? 'attach'
    });
  }

  trackRow(index: number, row: any): string | number {
    return row.id ?? row.requirementId ?? row.requirementResultId ?? index;
  }

  trackColumn(_: number, column: ChecklistTableColumn): string {
    return column.value;
  }

  private matchesSectionType(groupType: ChecklistTableType, section: ChecklistTableSectionConfig): boolean {
    return [section.type, ...(section.typeAliases ?? [])].some(type => this.matchesType(groupType, type));
  }

  private matchesType(left: ChecklistTableType, right: ChecklistTableType): boolean {
    return left === right || `${left}`.toLowerCase() === `${right}`.toLowerCase();
  }
}
